import { EVENTS } from "../data/content";
import { _lcs, locale } from "../i18n";

/**
 * Events — the guest-app's view of what is on during the stay.
 *
 * Loads the accommodation's event feed from the core-api
 * (`GET /event/:accommodationId`, the public payload: published, still
 * upcoming, own events plus the regional pool) and turns it into the flat card
 * shape the screens render. Same split of duties as core/Weather.js: the API
 * returns data (ISO timestamps, enum keys, numbers), this class turns it into
 * wording in the guest language, a date chip and a gradient — which keeps the endpoint usable
 * by the client-admin, which words things differently.
 *
 * Without a resolved tenant (bundled demo mode) the screens fall back to the
 * demo events in data/content.js. A resolved tenant with an empty feed does
 * NOT fall back — "no events yet" is a real answer and demo content would be a
 * lie about a real house.
 */
export class Events
{
    /**
     * Category -> gradient key (`.ga-photo--<key>`). Event imagery is tenant
     * content we may not have (and imported images come with licence strings),
     * so the card art FALLS BACK to a tokenised gradient that re-tints under a
     * white-label theme — same approach as the demo content. An event that does
     * carry images shows the first one (its header image) instead.
     */
    static PHOTOS = {
        concert     : "dusk",
        party       : "dusk",
        festival    : "ember",
        market      : "culture",
        theatre     : "lavender",
        show        : "lavender",
        comedy      : "ember",
        cinema      : "dusk",
        exhibition  : "culture",
        literature  : "culture",
        talk        : "culture",
        guided_tour : "valley",
        sport       : "valley",
        family      : "ember",
        food        : "wine",
        workshop    : "lavender",
        tradition   : "culture",
        other       : "coast"
    };

    /**
     * The radius the API applies to a guest's feed by default — mirrors
     * core-api GeoQuery.GUEST_RADIUS_KM, and only used to word it.
     */
    static RADIUS_KM = 50;

    /** Short weekday in the current language ("Sa", "Sat", "сб", "so"). */
    static weekday( date )
    {
        return date.toLocaleDateString( locale(), { weekday : "short" } );
    }

    constructor( app )
    {
        this.app = app;

        // Raw API events (public payload), newest-first by next date. Within
        // the API's default guest radius (core-api GeoQuery.GUEST_RADIUS_KM) —
        // this is the feed the home screen, the tab and the detail use.
        this.items = [];
        this.loaded = false;
        this.failed = false;

        // The same feed without a radius, fetched only when a guest asks the
        // events tab for what is further away. Kept apart so "Als Nächstes"
        // on the home screen never points two hours' drive away.
        this.wideItems = null;
        // The tab's choice, kept for the session so a detail and back does not
        // snap a guest who asked for everything back to the nearby list.
        this.showWide = false;

        this._pending = null;
    }

    /* -------------------------------- Loading -------------------------------- */

    /**
     * Resolve the event feed. Concurrent callers share one request. Never
     * throws — a failed load leaves the previous items in place and flags
     * `failed` so a screen can say so.
     *
     * @param {boolean} [force=false] Refetch even when already loaded.
     */
    async load( force = false )
    {
        if ( !force && this.loaded )
        {
            return this.items;
        }
        if ( this._pending )
        {
            return await this._pending;
        }

        this._pending = this._fetch().finally( () => { this._pending = null; } );
        return await this._pending;
    }

    async _fetch()
    {
        const accommodation = this.app.accommodation;
        if ( !accommodation )
        {
            // No tenant resolved (bundled demo mode) — nothing to fetch, and
            // the screens use the demo events instead.
            this.loaded = true;
            this.failed = false;
            return this.items;
        }

        try
        {
            const { result } = await this.app.api.get( '/event/' + accommodation.id );
            if ( result && 200 === +result.status && Array.isArray( result.data ) )
            {
                this.items = result.data;
                this.loaded = true;
                this.failed = false;
                return this.items;
            }
        }
        catch ( e )
        {
            console.error( e );
        }

        this.failed = true;
        return this.items;
    }

    /**
     * The feed without the radius (`?radius=0`). Null when it could not be
     * loaded — the tab then stays on the nearby list.
     */
    async loadWide()
    {
        const accommodation = this.app.accommodation;
        if ( !accommodation )
        {
            return null;
        }

        try
        {
            const { result } = await this.app.api.get( '/event/' + accommodation.id + '?radius=0' );
            if ( result && 200 === +result.status && Array.isArray( result.data ) )
            {
                this.wideItems = result.data;
                return this.wideItems;
            }
        }
        catch ( e )
        {
            console.error( e );
        }
        return null;
    }

    /* ------------------------------- Accessors ------------------------------- */

    /** True while no tenant is resolved — the screens then show demo events. */
    isDemo()
    {
        return !this.app.accommodation;
    }

    /**
     * Every event as a card view model, ready for the list template.
     *
     * @param {boolean} [wide=false] The feed without the radius, once loadWide()
     *                               has it — otherwise the nearby one.
     */
    getCards( wide = false )
    {
        if ( this.isDemo() )
        {
            return EVENTS;
        }
        const items = wide && this.wideItems ? this.wideItems : this.items;
        return items.map( ( event ) => Events.toCard( event ) );
    }

    /**
     * "Als Nächstes": the first event that has not begun yet. One that has —
     * an exhibition that opened last week, this morning's guided walk — may
     * still be on, but it is not next.
     */
    getNextCard()
    {
        const cards = this.getCards();
        return cards.find( ( card ) => !card.started ) || null;
    }

    /** One event as a detail view model, or null when it is not in the feed. */
    getDetail( id )
    {
        if ( this.isDemo() )
        {
            const demo = EVENTS.find( ( e ) => e.id === id );
            if ( !demo )
            {
                return null;
            }
            // Fill the detail-only fields the demo content does not carry, so
            // the template renders one shape either way.
            return {
                ...demo,
                subtitle    : "",
                description : demo.desc,
                images      : [],
                dateText    : _lcs( "{day}. {mon} · {time} Uhr",
                    { day : demo.day, mon : demo.mon, time : demo.time } ),
                facts       : [],
                dates       : [],
                links       : [],
                organizer   : "",
                directions  : "",
                address     : "",
                sourceUrl   : ""
            };
        }

        const event = this.items.find( ( e ) => e.id === id ) ||
            ( this.wideItems || [] ).find( ( e ) => e.id === id );
        return event ? Events.toDetail( event ) : null;
    }

    /**
     * One event straight from the API — the detail screen's fallback when the
     * feed was never loaded (deep link, reload, shared URL).
     */
    async fetchDetail( id )
    {
        const accommodation = this.app.accommodation;
        if ( !accommodation )
        {
            return null;
        }

        try
        {
            const { result } = await this.app.api.get( '/event/' + accommodation.id + '/' + id );
            if ( result && 200 === +result.status && result.data )
            {
                return Events.toDetail( result.data );
            }
        }
        catch ( e )
        {
            console.error( e );
        }
        return null;
    }

    /* ------------------------------ Presentation ----------------------------- */

    /** API event -> list card (the shape the demo content already uses). */
    static toCard( event )
    {
        const occurrence = event.nextOccurrence;
        const start = occurrence ? new Date( occurrence.startsAt ) : null;
        const cover = Events.cover( event );

        return {
            id    : event.id,
            title : event.title,
            desc  : Events.summary( event ),
            // The API sends the category's German title; it is a dictionary key.
            cat   : event.categoryTitle ? _lcs( event.categoryTitle ) : _lcs( "Veranstaltung" ),
            scope : event.isOwnEvent ? "own" : "region",
            day   : start ? String( start.getDate() ).padStart( 2, "0" ) : "–",
            mon   : start ? Events.month( start ) : "",
            time  : Events.time( occurrence ),
            // The template words the time itself — "18:30 Uhr" is German
            // sentence-building, not a clock format.
            allDay : occurrence ? true === occurrence.isAllDay : false,
            loc   : Events.place( event ),
            dist  : Events.distance( event ),
            photo : Events.PHOTOS[ event.category ] || Events.PHOTOS.other,
            icon  : event.categoryIcon || "calendar",
            // Begun and not over (core-api EventFactory.hasStarted). "Läuft
            // gerade" only with a stated end still ahead; without one, all we
            // know is that it has begun — it stays listed to the end of its day.
            started : Events.status( occurrence ) !== "",
            running : !!occurrence && occurrence.isRunning === true,
            status : Events.status( occurrence ),
            badge : !start ? "" : ( Events.status( occurrence ) || Events.relativeDay( start ) ),
            image : cover ? cover.url : "",
            imageAlt : cover ? cover.alt : ""
        };
    }

    /**
     * API event -> detail view model: the card plus the facts a guest needs
     * before deciding to go (price, registration, age, accessibility), the
     * remaining dates of a series and the outbound links.
     */
    static toDetail( event )
    {
        const card = Events.toCard( event );
        const occurrence = event.nextOccurrence;

        return {
            ...card,
            subtitle    : event.subtitle || "",
            description : ( event.description || event.teaser || "" ).trim(),
            images      : Events.images( event ),
            dateText    : Events.dateText( occurrence ),
            facts       : Events.facts( event ),
            dates       : Events.furtherDates( event ),
            links       : Events.links( event ),
            organizer   : ( event.organizer && event.organizer.name ) || "",
            directions  : ( event.location && event.location.directions ) || "",
            address     : Events.address( event ),
            sourceUrl   : event.sourceUrl || ""
        };
    }

    /**
     * The event's images in display order, ready for the gallery: entries
     * without a URL are dropped (an importer may deliver an image record it
     * could not resolve), and every entry gets an alt text — the title is a
     * better fallback for a screen reader than nothing at all.
     */
    static images( event )
    {
        const list = Array.isArray( event.images ) ? event.images : [];
        return list
            .filter( ( image ) => image && image.url )
            .map( ( image ) => ( {
                url    : image.url,
                alt    : image.alt || event.title || "",
                credit : image.credit || ""
            } ) );
    }

    /** The header image: first entry of the list, set in the client-admin. */
    static cover( event )
    {
        const list = Events.images( event );
        return list.length > 0 ? list[ 0 ] : null;
    }

    /** Short line under the card title. */
    static summary( event )
    {
        const text = ( event.teaser || event.subtitle || event.description || "" ).trim();
        if ( text.length <= 160 )
        {
            return text;
        }
        return text.slice( 0, 160 ).replace( /\s+\S*$/, "" ) + " …";
    }

    /** "18:30" — empty for an all-day occurrence (the caller words that). */
    static time( occurrence )
    {
        if ( !occurrence || occurrence.isAllDay )
        {
            return "";
        }
        const start = new Date( occurrence.startsAt );
        return String( start.getHours() ).padStart( 2, "0" ) + ":" +
               String( start.getMinutes() ).padStart( 2, "0" );
    }

    /** "Sa, 14. Jun · 18:30 – 22:00 Uhr" for the detail screen. */
    static dateText( occurrence )
    {
        if ( !occurrence )
        {
            return _lcs( "Termin folgt" );
        }

        const start = new Date( occurrence.startsAt );
        // Intl orders weekday / day / month per language — a hand-built
        // "Sa, 14. Jun" reads wrong in en, ru and cs.
        const day = start.toLocaleDateString( locale(), {
            weekday : "short",
            day     : "numeric",
            month   : "short"
        } );

        if ( occurrence.isAllDay )
        {
            return _lcs( "{day} · ganztägig", { day : day } );
        }

        let time = Events.time( occurrence );
        if ( occurrence.endsAt )
        {
            const end = new Date( occurrence.endsAt );
            if ( end.toDateString() === start.toDateString() )
            {
                time += " – " + String( end.getHours() ).padStart( 2, "0" ) + ":" +
                        String( end.getMinutes() ).padStart( 2, "0" );
            }
        }
        return _lcs( "{day} · {time} Uhr", { day : day, time : time } );
    }

    /** Every date after the next one — what makes a series visible as a series. */
    static furtherDates( event )
    {
        const list = Array.isArray( event.occurrences ) ? event.occurrences : [];
        const nextId = event.nextOccurrence ? event.nextOccurrence.id : null;

        return list
            .filter( ( o ) => o.id !== nextId )
            .map( ( o ) => ( {
                text     : Events.dateText( o ),
                isSoldOut : o.isSoldOut === true,
                isCancelled : o.status === "cancelled"
            } ) );
    }

    /**
     * The practical facts, in the order a guest asks them: what does it cost,
     * do I need to sign up, is it for me, can I get in.
     */
    static facts( event )
    {
        const facts = [];

        const price = Events.price( event );
        if ( price )
        {
            facts.push( { icon: "badge-euro", text: price } );
        }

        switch ( event.bookingType )
        {
            case "registration": facts.push( { icon: "user-round", text: _lcs( "Anmeldung erforderlich" ) } ); break;
            case "ticket":       facts.push( { icon: "ticket", text: _lcs( "Ticket erforderlich" ) } ); break;
            case "reservation":  facts.push( { icon: "calendar-check", text: _lcs( "Reservierung erforderlich" ) } ); break;
            default: break;
        }

        const audience = event.audience || {};
        if ( audience.minAge )
        {
            facts.push( { icon: "user-round", text: _lcs( "Ab {age} Jahren", { age: audience.minAge } ) } );
        }
        if ( audience.isFamilyFriendly )
        {
            facts.push( { icon: "users", text: _lcs( "Für Familien geeignet" ) } );
        }

        if ( event.isOutdoor )
        {
            facts.push( { icon: "cloud-sun", text: _lcs( "Findet draußen statt" ) } );
        }

        if ( Array.isArray( event.accessibility ) && event.accessibility.length > 0 )
        {
            facts.push( { icon: "check-circle-2", text: _lcs( "Barrierefrei zugänglich" ) } );
        }

        if ( event.nextOccurrence && event.nextOccurrence.isSoldOut )
        {
            facts.push( { icon: "triangle-alert", text: _lcs( "Ausverkauft" ) } );
        }

        return facts;
    }

    /** "Eintritt frei" / "ab 24,00 €" / "" when no price was stated. */
    static price( event )
    {
        const pricing = event.pricing;
        if ( !pricing || !pricing.type )
        {
            return "";
        }

        const currency = pricing.currency || "EUR";
        const amount = ( value ) => Number( value ).toLocaleString( locale(), {
            style : "currency",
            currency : currency,
            maximumFractionDigits : 2
        } );

        switch ( pricing.type )
        {
            case "free":     return _lcs( "Eintritt frei" );
            case "donation": return _lcs( "Eintritt auf Spendenbasis" );
            case "unknown":  return "";
            case "fixed":    return pricing.priceMin != null ? amount( pricing.priceMin ) : "";
            case "from":     return pricing.priceMin != null
                ? _lcs( "ab {amount}", { amount: amount( pricing.priceMin ) } ) : "";
            case "range":
                if ( pricing.priceMin != null && pricing.priceMax != null )
                {
                    return amount( pricing.priceMin ) + " – " + amount( pricing.priceMax );
                }
                return pricing.priceMin != null
                    ? _lcs( "ab {amount}", { amount: amount( pricing.priceMin ) } ) : "";
            default: return "";
        }
    }

    /** Outbound links (tickets, website, provider attribution). */
    static links( event )
    {
        const links = [];
        if ( event.ticketUrl )
        {
            links.push( { url: event.ticketUrl, label: _lcs( "Tickets" ), icon: "ticket" } );
        }
        if ( event.websiteUrl )
        {
            links.push( { url: event.websiteUrl, label: _lcs( "Zur Website" ), icon: "arrow-right" } );
        }
        // Imported events link back to the provider — usually a licence condition.
        if ( !event.ticketUrl && !event.websiteUrl && event.sourceUrl )
        {
            links.push( { url: event.sourceUrl, label: _lcs( "Mehr erfahren" ), icon: "arrow-right" } );
        }
        return links;
    }

    static place( event )
    {
        if ( event.locationType === "online" )
        {
            return "Online";
        }

        const location = event.location || {};
        return ( location.name || location.city || _lcs( "Ort wird noch bekannt gegeben" ) );
    }

    static address( event )
    {
        const location = event.location || {};
        const line = [ location.street, [ location.postalCode, location.city ].filter( Boolean ).join( " " ) ]
            .filter( ( p ) => p && String( p ).trim() )
            .join( ", " );
        return line;
    }

    /** "1,2 km" from the house, or where the event starts at the house itself. */
    static distance( event )
    {
        if ( typeof event.distanceKm === "number" )
        {
            return _lcs( "{n} km", {
                n : event.distanceKm.toLocaleString( locale(), { maximumFractionDigits: 1 } )
            } );
        }
        return event.isOwnEvent ? _lcs( "Am Haus" ) : "";
    }

    /** Short month without a trailing dot ("Jun", not "Jun.") — the date chip. */
    static month( date )
    {
        return date.toLocaleDateString( locale(), { month: "short" } ).replace( ".", "" );
    }

    /** "Läuft gerade" / "Hat begonnen" for a date that is on, else "". */
    static status( occurrence )
    {
        if ( !occurrence || occurrence.hasStarted !== true )
        {
            return "";
        }
        return occurrence.isRunning === true ? _lcs( "Läuft gerade" ) : _lcs( "Hat begonnen" );
    }

    /** "Heute" / "Morgen" / short weekday — the badge on a card. */
    static relativeDay( date )
    {
        const startOfDay = ( d ) => new Date( d.getFullYear(), d.getMonth(), d.getDate() ).getTime();
        // Rounded whole days, so a DST change does not shift the label.
        const days = Math.round( ( startOfDay( date ) - startOfDay( new Date() ) ) / 86400000 );

        if ( 0 === days ) return _lcs( "Heute" );
        if ( 1 === days ) return _lcs( "Morgen" );
        return Events.weekday( date );
    }
}
